import React from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Flame,
  ChevronLeft,
  ArrowRight,
  Check,
  Users,
  Mail,
  HandHeart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { ministries } from '../data/ministries';

const MinistryDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const m = ministries.find((x) => x.slug === slug);
  if (!m) return <Navigate to="/ministries" replace />;
  const Icon = m.icon;

  const others = ministries.filter((x) => x.slug !== m.slug);

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/ministries')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> All ministries
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1030]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${m.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1030]/70 via-[#0a1030]/60 to-[#0a1030]/95" />
        <div className="relative max-w-[1000px] mx-auto text-center px-6 py-24 md:py-36 text-white">
          <div className="h-14 w-14 mx-auto rounded-full bg-white/10 border border-white/25 flex items-center justify-center mb-6 backdrop-blur">
            <Icon className="h-6 w-6 text-[#c8b98a]" strokeWidth={2} />
          </div>
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-4">
            Ministry
          </p>
          <h1
            className="text-[46px] md:text-[80px] lg:text-[92px] leading-[1.02]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            {m.name}
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] text-white/85 max-w-[640px] mx-auto italic">
            {m.tagline}
          </p>
        </div>
      </section>

      {/* Intro + What we do */}
      <section className="py-20 md:py-28 px-4 md:px-6">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[18px] md:text-[20px] text-[#3a3a3a] leading-[1.75] max-w-[720px] mx-auto">
              {m.intro}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8 md:p-12">
            <h2
              className="text-[30px] md:text-[38px] text-[#0a1030] leading-tight"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              What we do
            </h2>
            <ul className="mt-6 space-y-4">
              {m.what.map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <div className="h-6 w-6 flex-shrink-0 rounded-full bg-[#0a1030] text-[#e9d9a0] flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </div>
                  <span className="text-[16px] md:text-[17px] text-[#3a3a3a] leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-10 pt-8 border-t border-black/10">
              <h3
                className="text-[24px] md:text-[28px] text-[#0a1030] mb-3"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                Who's it for?
              </h3>
              <p className="text-[16px] text-[#3a3a3a] leading-relaxed">
                {m.who}
              </p>
            </div>
          </div>

          {/* Scripture */}
          <div className="mt-10 pl-6 border-l-2 border-[#c8b98a]">
            <p className="italic text-[18px] md:text-[20px] leading-relaxed text-[#3a3a3a]">
              {m.verse.text}
            </p>
            <p className="mt-3 text-[13px] font-semibold tracking-wide text-[#8a7a4a] uppercase">
              {m.verse.ref}
            </p>
          </div>

          {/* Support this ministry — shown only when a giveFund is defined */}
          {m.giveFund && (
            <div className="mt-14 bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-5">
                <div className="md:col-span-2 bg-[#0a1030] text-white p-8 md:p-10 flex flex-col justify-center">
                  <div className="h-11 w-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
                    <HandHeart className="h-5 w-5 text-[#c8b98a]" strokeWidth={2} />
                  </div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#c8b98a] mb-2">
                    Give
                  </p>
                  <h3
                    className="text-[28px] md:text-[34px] leading-tight"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    Support {m.name}
                  </h3>
                </div>
                <div className="md:col-span-3 p-8 md:p-10">
                  <p className="text-[16px] md:text-[17px] text-[#3a3a3a] leading-relaxed">
                    Want to help fuel this ministry? Every gift directly supports
                    our {m.name.toLowerCase()} work — from community events to
                    the people we serve alongside. Tap Give and choose the{' '}
                    <span className="font-semibold text-[#0a1030]">
                      {m.giveFund}
                    </span>{' '}
                    fund on the next screen.
                  </p>
                  <Button
                    asChild
                    className="mt-6 rounded-full bg-[#1c2b6b] hover:bg-[#141f52] text-white h-12 px-8 text-[14px] font-semibold"
                  >
                    <Link to={`/give?fund=${encodeURIComponent(m.giveFund)}`}>
                      <HandHeart className="h-4 w-4 mr-2" /> Give to {m.name}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Get involved CTA */}
      <section className="px-4 md:px-6 pb-20 md:pb-24">
        <div className="max-w-[900px] mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-[#0a1030] text-white p-10 md:p-14 text-center">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="relative">
              <div className="h-14 w-14 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                <Users className="h-6 w-6 text-[#c8b98a]" strokeWidth={2} />
              </div>
              <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-4">
                Get Involved
              </p>
              <h3
                className="text-[30px] md:text-[40px] leading-tight max-w-[620px] mx-auto"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                Want to be part of {m.name}?
              </h3>
              <p className="mt-4 text-white/80 max-w-[520px] mx-auto">
                Reach out and someone from our team will connect with you
                personally.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  className="rounded-full bg-[#e9e3d7] hover:bg-white text-[#0a1030] px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/contact">
                    <Mail className="h-4 w-4 mr-2" /> Contact us
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/plan-a-visit">Plan a visit</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other ministries */}
      <section className="px-4 md:px-6 pb-24 md:pb-32">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10">
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a7a4a] font-semibold mb-3">
              Explore
            </p>
            <h3
              className="text-[30px] md:text-[42px] text-[#0a1030] leading-tight"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              Other Ministries
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/ministries/${o.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={o.image}
                    alt={o.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div
                    className="text-[20px] text-[#0a1030] leading-tight"
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                  >
                    {o.name}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-[#0a1030] group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MinistryDetail;
